/**
 * Student-detail billing surfaces injected into People (11F.7).
 *
 * People must not import Billing (eslint feature boundary), so these are
 * mounted from the route composition layer — the same pattern
 * `StudentBillingTab` already uses.
 *
 * Two exports:
 *   - `StudentSpendInsight`   → one Insight cell in the left rail
 *   - `StudentOverviewBilling` → credit balance + Invoiced/Paid/Due summary
 *     + recent invoice rows, in the Overview tab body (ref `student1.png`)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { CardHeader, CardTitle, RailInsight } from '@/components/common';
import {
  useInvoices,
  useStudentBalance,
  type InvoiceResponse,
} from '@features/invoices/api/billing';
import { formatMinorAmount, invoiceStatusLabel } from '@features/invoices/config/ui';

/** Total ever invoiced-and-paid for this student. */
export const StudentSpendInsight: React.FC<{ studentId: string }> = ({ studentId }) => {
  const { data } = useInvoices({ studentId, limit: 200 });
  const spend = (data?.items ?? []).reduce(
    (sum, inv) => (inv.status === 'void' ? sum : sum + inv.amount_paid),
    0,
  );
  return (
    <RailInsight
      label="Total spend"
      value={formatMinorAmount(spend)}
      testId="candeza-student-rail-spend"
    />
  );
};

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SummaryCell = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-variant-numeric: tabular-nums;
`;

const InvoiceRow = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  color: inherit;
  text-decoration: none;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CreditNote = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.successLight};
  color: #15803d;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const StudentOverviewBilling: React.FC<{ studentId: string }> = ({ studentId }) => {
  const { data, isLoading } = useInvoices({ studentId, limit: 100 });
  const { data: balance } = useStudentBalance(studentId);

  const invoices: InvoiceResponse[] = (data?.items ?? []).filter(
    (i) => i.status !== 'draft' && i.status !== 'void',
  );
  const invoiced = invoices.reduce((s, i) => s + i.total, 0);
  const paid = invoices.reduce((s, i) => s + i.amount_paid, 0);
  const due = invoices.reduce((s, i) => s + i.amount_due, 0);

  const recent = [...invoices]
    .sort((a, b) => new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime())
    .slice(0, 5);

  // A NEGATIVE ledger balance means the student is in credit (they've paid
  // ahead) — see the ledger module's sign convention. Only surfaced when it
  // actually exists; a zero balance needs no callout.
  const creditPaise = balance && balance.balance < 0 ? Math.abs(balance.balance) : 0;

  return (
    <div>
      <CardHeader style={{ paddingLeft: 0 }}>
        <CardTitle>Invoices</CardTitle>
      </CardHeader>

      {creditPaise > 0 && (
        <CreditNote data-testid="candeza-student-credit-balance">
          {formatMinorAmount(creditPaise)} credit on account
        </CreditNote>
      )}

      <SummaryRow data-testid="candeza-student-invoice-summary">
        <SummaryCell>
          <SummaryLabel>Invoiced</SummaryLabel>
          <SummaryValue>{formatMinorAmount(invoiced)}</SummaryValue>
        </SummaryCell>
        <SummaryCell>
          <SummaryLabel>Paid</SummaryLabel>
          <SummaryValue style={{ color: '#15803d' }}>
            {formatMinorAmount(paid)}
          </SummaryValue>
        </SummaryCell>
        <SummaryCell>
          <SummaryLabel>Due</SummaryLabel>
          <SummaryValue style={{ color: due > 0 ? '#b45309' : undefined }}>
            {formatMinorAmount(due)}
          </SummaryValue>
        </SummaryCell>
      </SummaryRow>

      {isLoading ? (
        <p style={{ color: '#64748b', fontSize: 14 }}>Loading invoices…</p>
      ) : recent.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>No invoices raised yet.</p>
      ) : (
        <div data-testid="candeza-student-recent-invoices">
          {recent.map((inv) => (
            <InvoiceRow key={inv.id} to={`/invoices/${inv.id}`}>
              <span>
                {inv.number ?? 'Draft'}
                <span style={{ color: '#94a3b8' }}>
                  {' · '}
                  {new Date(inv.issue_date).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              </span>
              <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ color: '#64748b' }}>{invoiceStatusLabel(inv.status)}</span>
                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMinorAmount(inv.total, inv.currency)}
                </strong>
              </span>
            </InvoiceRow>
          ))}
        </div>
      )}
    </div>
  );
};
